import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Loader2, RefreshCw } from 'lucide-react';
import { apiFetch } from '../../../utils/api';

const ReportingDashboard: React.FC = () => {
    const [pipeline, setPipeline] = useState<any[]>([]);
    const [burn, setBurn] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);


    const fetchData = async () => {
        setLoading(true);
        try {
            const [pipeRes, burnRes] = await Promise.all([
                apiFetch('/reporting/pipeline-summary'),
                apiFetch('/reporting/expense-burn')
            ]);

            if (pipeRes.ok) setPipeline(await pipeRes.json());
            if (burnRes.ok) setBurn(await burnRes.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

    const totalPipelineValue = pipeline.reduce((sum, item) => sum + parseFloat(item.total_value || 0), 0);

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Business Intelligence</h2>
                    <p className="text-gray-500">Sales pipeline and financial performance analytics</p>
                </div>
                <button onClick={fetchData} className="p-3 bg-white border rounded-xl hover:bg-gray-50 transition">
                    <RefreshCw size={20} className="text-gray-400" />
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Sales Pipeline */}
                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                            <TrendingUp size={24} />
                        </div>
                        <h3 className="text-xl font-bold">Sales Pipeline by Stage</h3>
                    </div>

                    <div className="space-y-6">
                        {pipeline.map((item, i) => (
                            <div key={i} className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="font-bold text-gray-600">{item.stage}</span>
                                    <span className="font-bold">${parseFloat(item.total_value || 0).toLocaleString()}</span>
                                </div>
                                <div className="h-3 bg-gray-50 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full"
                                        style={{ width: `${(parseFloat(item.total_value || 0) / (totalPipelineValue || 1)) * 100}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Expense Burn */}
                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-3 bg-red-50 text-red-600 rounded-xl">
                            <DollarSign size={24} />
                        </div>
                        <h3 className="text-xl font-bold">Monthly Expense Burn</h3>
                    </div>

                    <div className="flex items-end gap-2 h-48">
                        {burn.slice().reverse().map((item, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-2 group">
                                <div
                                    className="w-full bg-red-400 rounded-t-lg transition-all group-hover:bg-red-500"
                                    style={{ height: `${(parseFloat(item.total_amount) / Math.max(...burn.map(b => parseFloat(b.total_amount)))) * 100}%` }}
                                ></div>
                                <span className="text-[10px] text-gray-400 font-bold rotate-45 mt-2">{item.month}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Performance KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 rounded-3xl text-white shadow-lg shadow-blue-200">
                    <div className="text-blue-100 text-xs font-bold uppercase mb-1">Total Pipeline Value</div>
                    <div className="text-3xl font-bold">${totalPipelineValue.toLocaleString()}</div>
                    <div className="mt-4 text-xs text-blue-100 font-medium">+12.5% from last month</div>
                </div>
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="text-gray-400 text-xs font-bold uppercase mb-1">Active Opportunities</div>
                    <div className="text-3xl font-bold text-gray-800">{pipeline.reduce((sum, item) => sum + parseInt(item.count || 0), 0)}</div>
                    <div className="mt-4 text-xs text-emerald-500 font-bold">Healthy Velocity</div>
                </div>
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="text-gray-400 text-xs font-bold uppercase mb-1">Avg. Expense/Project</div>
                    <div className="text-3xl font-bold text-gray-800">$2,450</div>
                    <div className="mt-4 text-xs text-amber-500 font-bold">In Policy Range</div>
                </div>
            </div>
        </div>
    );
};

export default ReportingDashboard;
